import { useEffect, useState, useRef, useCallback, ReactNode } from "react";
import { ControlledConnectionsList } from "../controlled-components/ControlledConnectionsList";
import { ControlledConnectionForm } from "../controlled-components/ControlledConnectionForm";
import { ControlledOauthConnection } from "../controlled-components/ControlledOauthConnection";

const FADE_MS = 150;

function FadeTransition({ viewKey, children, onSwap }: { viewKey: string; children: ReactNode; onSwap?: (newKey: string) => void }) {
  const [displayedKey, setDisplayedKey] = useState(viewKey);
  const [displayedChildren, setDisplayedChildren] = useState(children);
  const [opacity, setOpacity] = useState(1);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (viewKey === displayedKey) {
      // Same view, just update children in place
      setDisplayedChildren(children);
      return;
    }

    // New view: fade out, swap, fade in
    clearTimeout(timeoutRef.current);
    setOpacity(0);

    timeoutRef.current = setTimeout(() => {
      setDisplayedKey(viewKey);
      setDisplayedChildren(children);
      onSwap?.(viewKey);
      // Force a reflow before fading in
      requestAnimationFrame(() => {
        setOpacity(1);
      });
    }, FADE_MS);

    return () => clearTimeout(timeoutRef.current);
  }, [viewKey, children, displayedKey, onSwap]);

  return (
    <div
      className="w-full flex-1 flex flex-col min-h-0"
      style={{
        opacity,
        transition: `opacity ${FADE_MS}ms ease-in-out`,
      }}
    >
      {displayedChildren}
    </div>
  );
}

export const MainSection = ({
  setIsWhiteLabel,
  isWhiteLabel,
  setIsCompact,
}: {
  setIsWhiteLabel: (value: boolean | null) => void;
  isWhiteLabel: boolean | null;
  setIsCompact?: (value: boolean) => void;
}) => {
  interface MessageData {
    linkTokenEndpoint: string;
    linkHeaders?: Record<string, unknown>;
    title?: string;
    selectedConnection?: number | string;
    showNameInput?: boolean;
    imageUrl?: string;
    companyName?: string;
  }

  const [messageData, setMessageData] = useState<MessageData>();
  const [integrationId, setIntegrationId] = useState<number | undefined>();
  const [clientId, setClientId] = useState<string | undefined>();
  const [secretId, setSecretId] = useState<string | undefined>();
  const [connectionGuide, setConnectionGuide] = useState<string>();
  const [sessionId, setSessionId] = useState<string>("");
  const [requestId, setRequestId] = useState<number>();
  const [useDefaultOauthCreds, setUseDefaultOauthCreds] = useState<boolean>(false);

  const handleSwap = useCallback((newKey: string) => {
    setIsCompact?.(newKey !== 'list' && newKey !== 'loading');
  }, [setIsCompact]);

  useEffect(() => {
    const queryParameters = new URLSearchParams(
      window?.location?.href.split("?")[1]
    );

    const base64Decoded = queryParameters.get("data") || "";

    if (base64Decoded) {
      const jsonDecoded = atob(base64Decoded);
      const decodedObject = JSON.parse(jsonDecoded);
      setMessageData(decodedObject);
      setIntegrationId(undefined);
    }
  }, []);

  // Derive a stable view key for the fade transition
  const isOauth = !!(secretId && (clientId || useDefaultOauthCreds) && integrationId && messageData && requestId);
  const isForm = !!(integrationId && messageData && !clientId && !useDefaultOauthCreds);
  const isList = !!(!integrationId && messageData && !clientId);

  const viewKey = isOauth ? 'oauth' : isForm ? 'form' : isList ? 'list' : 'loading';

  let content: ReactNode = null;

  if (isOauth) {
    content = (
      <ControlledOauthConnection
        integrationId={integrationId!}
        setIntegrationId={setIntegrationId}
        secretId={secretId!}
        setClientId={setClientId}
        showNameInput={messageData?.showNameInput}
        selectedConnection={messageData?.selectedConnection}
        connectionGuide={connectionGuide}
        requestId={requestId!}
        linkHeaders={messageData!.linkHeaders}
        imageUrl={messageData?.imageUrl}
        companyName={messageData?.companyName}
        isWhiteLabel={isWhiteLabel}
      />
    );
  } else if (isForm) {
    content = (
      <ControlledConnectionForm
        definitionId={integrationId!}
        setIntegrationId={setIntegrationId}
        selectedConnection={messageData!.selectedConnection}
        showNameInput={messageData!.showNameInput ?? false}
        connectionGuide={connectionGuide}
        requestId={requestId}
        linkHeaders={messageData!.linkHeaders}
        isWhiteLabel={isWhiteLabel}
      />
    );
  } else if (isList) {
    content = (
      <ControlledConnectionsList
        linkTokenEndpoint={messageData!.linkTokenEndpoint}
        linkHeaders={messageData!.linkHeaders}
        setIntegrationId={setIntegrationId}
        setClientId={setClientId}
        setSecretId={setSecretId}
        setConnectionGuide={setConnectionGuide}
        title={messageData!.title}
        selectedConnection={messageData!.selectedConnection}
        setSessionId={setSessionId}
        sessionId={sessionId}
        setRequestId={setRequestId}
        setUseDefaultOauthCreds={setUseDefaultOauthCreds}
        setIsWhiteLabel={setIsWhiteLabel}
      />
    );
  }

  return (
    <FadeTransition viewKey={viewKey} onSwap={handleSwap}>
      {content}
    </FadeTransition>
  );
};
